import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { isEmpty } from 'lodash';
import { Button, Space, Divider } from 'antd';
import { ExportCheckbox } from '@src/components/ActionToolbar/More/ExportOption/components/ExportCheckbox';
import { ExportCheckboxGroup } from '@src/components/ActionToolbar/More/ExportOption/components/ExportCheckboxGroup';
import { exportDataAtom } from '@src/components/export/atom';
import { hiddenOptions, optionNameMapping, sectionDisplayOrder } from './constants';
import { ExportAllButton } from './components/ExportAllButton';
import { ContentArea } from './styled';

interface RegularExportProps {
  onClose: () => void;
  visible: boolean;
  onFooterRender?: (footer: React.ReactNode) => void;
}

const RegularExport: React.FC<RegularExportProps> = ({ onClose, visible, onFooterRender }) => {
  const [sections, setSections] = useState<string[]>([]);
  const [selectedDimensionTables, setSelectedDimensionTables] = useState<string[]>([]);
  const initSections = useRef<string[]>([]);
  const dimensionTableOptions = useRef<{ value: string; label: string }[]>([]);

  const exportData: any = useAtomValue(exportDataAtom);

  const handleSectionChange = useCallback(
    (section: string) => {
      let newSections = [...sections];
      if (sections.includes(section)) {
        newSections = newSections.filter((i) => i !== section);
      } else {
        newSections.push(section);
      }
      setSections(newSections);
    },
    [sections],
  );

  const handleCheckboxGroupChange = useCallback(
    (key: string, selected: string[]) => {
      if (key === 'overview-dimension-tables') {
        setSelectedDimensionTables(selected);
        if (
          (!sections.includes(key) && selected.length === 0) ||
          (sections.includes(key) && selected.length > 0)
        ) {
          return;
        }
        handleSectionChange(key);
      }
    },
    [handleSectionChange, sections],
  );

  useEffect(() => {
    const res: string[] = [];
    initSections.current = [];
    dimensionTableOptions.current = [];
    const sectionsObj: Record<string, Record<string, any>> = exportData?.sections;
    if (!sectionsObj) {
      setSections(res);
      return;
    }
    sectionDisplayOrder.forEach((key) => {
      const value = sectionsObj[key] ?? {};
      if (key === 'overview-dimension-tables') {
        const dimensionTables = Object.keys(value).filter(
          (widgetKey) =>
            widgetKey.startsWith('dimension-table') || widgetKey === 'dimension-viewers-table',
        );
        if (dimensionTables.length > 0) {
          dimensionTableOptions.current = dimensionTables.map((tableKey) => {
            let label = tableKey;

            if (tableKey === 'dimension-viewers-table') {
              label = optionNameMapping['dimension-viewers-table'] || 'Top Impacted Viewers';
            } else if (tableKey.startsWith('dimension-table::')) {
              const widgetInfo = value[tableKey]?.widgetInfo;
              label = widgetInfo?.title || tableKey.split('::')[1];
            }

            return {
              value: tableKey,
              label: label,
            };
          });
          res.push('overview-dimension-tables');
        }
        return;
      }
      if (!isEmpty(value)) {
        res.push(key);
      }
    });
    const dimensionTables = dimensionTableOptions.current.map((table) => table.value);
    setSelectedDimensionTables(dimensionTables);
    initSections.current = res;
    setSections(res);
  }, [exportData?.sections, visible]);

  const content = initSections.current
    .map((key) => {
      if (hiddenOptions.includes(key)) {
        return null;
      }
      if (key === 'overview-dimension-tables' && dimensionTableOptions.current.length > 0) {
        return (
          <ExportCheckboxGroup
            key={key}
            title={optionNameMapping[key]}
            groupKey={key}
            options={dimensionTableOptions.current}
            selected={selectedDimensionTables}
            onSelectedChange={handleCheckboxGroupChange}
          />
        );
      }
      return (
        <ExportCheckbox
          title={optionNameMapping[key]}
          key={key}
          checked={sections.includes(key)}
          onChange={() => handleSectionChange(key)}
        />
      );
    })
    .filter((comp) => !!comp);

  const footerContent = (
    <>
      <Space style={{ float: 'right' }}>
        <Button onClick={onClose}>Cancel</Button>
        <ExportAllButton
          disabled={sections.length === 1}
          sections={sections}
          selectedDimensionTables={selectedDimensionTables}
          onDone={onClose}
        />
      </Space>
    </>
  );

  useEffect(() => {
    if (onFooterRender) {
      onFooterRender(footerContent);
    }
  }, [sections.length, selectedDimensionTables.length]);

  return <ContentArea>{content}</ContentArea>;
};

export default RegularExport;
